/**
 * Hook for fetching card rulings from Scryfall via Firebase Function
 *
 * Calls the getCardRulings callable function which caches results in Firestore.
 * Uses TanStack Query for client-side caching with long stale time.
 */
import { useQuery } from '@tanstack/react-query';
import { httpsCallable } from 'firebase/functions';

import { functions } from '@/services/firebase';

/**
 * Ruling item from Scryfall
 */
export interface CardRuling {
  source: 'wotc' | 'scryfall';
  published_at: string;
  comment: string;
}

/**
 * Response from getCardRulings function
 */
interface CardRulingsResponse {
  cardId: string;
  rulings: CardRuling[];
  cached: boolean;
}

/**
 * Fetch card rulings via Firebase callable function
 */
async function fetchCardRulings(cardId: string): Promise<CardRuling[]> {
  const callable = httpsCallable<{ cardId: string }, CardRulingsResponse>(functions, 'getCardRulings');
  const result = await callable({ cardId });
  return result.data.rulings;
}

/**
 * Hook for fetching card rulings
 *
 * @param cardId - Scryfall card ID (UUID)
 * @param options.enabled - Whether to fetch (default: true when cardId is provided)
 */
export default function useCardRulings(cardId: string | null | undefined, options?: { enabled?: boolean }) {
  const enabled = options?.enabled ?? !!cardId;

  return useQuery({
    queryKey: ['cardRulings', cardId],
    queryFn: () => fetchCardRulings(cardId!),
    enabled: enabled && !!cardId,
    staleTime: 1000 * 60 * 60 * 24, // 24 hours - rulings rarely change
    gcTime: 1000 * 60 * 60 * 24 * 7, // 7 days cache
    retry: 2,
  });
}
